/**
 * Supervisor Feed — Admin + PM real-time pulse on Layout/Animation
 * Supervisor verdicts (Feb 9, 2026 founder ask).
 *
 * Chronological list of every completed supervisor review with:
 *   - Artist name + shot code + supervisor kind
 *   - Score badge (64/70) + decision pill (APPROVED / MINOR_REVISION / ...)
 *   - Top 3 critical pins preview
 *   - "Open in Approvals" button → /reviews?submission_id=<id>
 *
 * Filterable by project / artist / decision / supervisor kind.
 * Auto-refreshes every 30s. New rows fade in at the top.
 *
 * Founder hard rule: NO "AI" in any user-visible text. Supervisors
 * appear as "Layout Supervisor" / "Animation Supervisor".
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Filter, RefreshCw, Eye, CheckCircle2, AlertTriangle,
  XCircle, AlertCircle, ChevronRight, Clock, MapPin, Film,
  Stethoscope, Wrench, Zap, Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/apiClient';

const DECISION_BADGES = {
  APPROVED:        { label: 'APPROVED',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: CheckCircle2 },
  MINOR_REVISION:  { label: 'MINOR REVISION', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',         icon: AlertCircle },
  MAJOR_REVISION:  { label: 'MAJOR REVISION', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/40',     icon: AlertTriangle },
  REJECTED:        { label: 'REJECTED',       cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40',           icon: XCircle },
};

const SEV_DOT = {
  CRITICAL: 'bg-rose-500',
  MAJOR:    'bg-orange-400',
  MINOR:    'bg-zinc-400',
};

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60)        return `${diff}s ago`;
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function SupervisorFeedPage() {
  const { api } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    project_id: '', supervisor_kind: '', decision: '',
  });
  const [now, setNow] = useState(Date.now());

  // ── Pipeline diagnostics (Feb 10 founder ask) ──
  const [diag, setDiag] = useState(null);
  const [diagOpen, setDiagOpen] = useState(true);
  const [busyIds, setBusyIds] = useState({});  // {sub_id|task_id: action}
  const [diagToast, setDiagToast] = useState('');

  // Load projects for the filter dropdown
  useEffect(() => {
    api.get('/projects')
      .then(r => setProjects(r.data?.items || r.data?.projects || r.data || []))
      .catch(() => setProjects([]));
  }, [api]);

  const buildQuery = useCallback(() => {
    const qs = [];
    if (filters.project_id) qs.push(`project_id=${encodeURIComponent(filters.project_id)}`);
    if (filters.supervisor_kind) qs.push(`supervisor_kind=${filters.supervisor_kind}`);
    if (filters.decision) qs.push(`decision=${filters.decision}`);
    qs.push('limit=50');
    return qs.join('&');
  }, [filters]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [feedR, diagR] = await Promise.all([
        api.get(`/reviews/supervisor-feed?${buildQuery()}`),
        api.get(`/reviews/supervisor-diagnostics${filters.project_id ? `?project_id=${filters.project_id}` : ''}`)
          .catch(() => ({ data: null })),
      ]);
      setItems(feedR.data?.items || []);
      setDiag(diagR.data || null);
    } catch (_e) {
      if (!silent) setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, buildQuery, filters.project_id]);

  const rerunSupervisor = useCallback(async (submissionId) => {
    setBusyIds(b => ({ ...b, [submissionId]: 'rerun' }));
    setDiagToast('');
    try {
      const r = await api.post(`/reviews/supervisor-rerun/${submissionId}`);
      setDiagToast(`✓ ${r.data?.tldr || 'Supervisor re-triggered'}`);
      // Refresh diagnostics after a brief delay
      setTimeout(() => load(true), 1200);
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') return;   // global toast handles it
      setDiagToast(`✗ ${fe.message}`);
    } finally {
      setBusyIds(b => { const { [submissionId]: _, ...rest } = b; return rest; });
      setTimeout(() => setDiagToast(''), 5000);
    }
  }, [api, load]);

  const forceTaskType = useCallback(async (taskId, taskType) => {
    setBusyIds(b => ({ ...b, [taskId]: 'force' }));
    setDiagToast('');
    try {
      const r = await api.post(`/reviews/admin/set-task-type/${taskId}`, { task_type: taskType });
      setDiagToast(`✓ ${r.data?.tldr || 'Task type updated'}`);
      setTimeout(() => load(true), 1000);
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') return;
      setDiagToast(`✗ ${fe.message}`);
    } finally {
      setBusyIds(b => { const { [taskId]: _, ...rest } = b; return rest; });
      setTimeout(() => setDiagToast(''), 5000);
    }
  }, [api, load]);

  // Batch-dispatch the supervisor for EVERY pending submission in the
  // current project filter. Founder spec Feb 11 2026: TLM had ~120
  // Layout submissions sitting with no AI review after the
  // ffmpeg+resolver fix; clicking Re-run one-by-one wasn't practical.
  const [batchBusy, setBatchBusy] = useState(false);
  // Mass-retry auto-fetch for tasks stuck at the NAS-fetch step.
  // Founder bug Jun 13 2026: the SupervisorFeedPage only showed
  // failures DOWNSTREAM of auto-fetch, so 115 of 122 tasks rotting at
  // auto_fetch_status="failed" were invisible. New stuck-at-fetch
  // section + this handler give the producer one click to retry them
  // all without leaving the page.
  const [fetchRetryBusy, setFetchRetryBusy] = useState(false);
  const retryStuckFetch = useCallback(async () => {
    if (!filters.project_id) {
      setDiagToast('✗ Pick a project from the filter first.');
      setTimeout(() => setDiagToast(''), 5000);
      return;
    }
    const count = diag?.stuck_at_fetch?.length || 0;
    if (count === 0) {
      setDiagToast('Nothing to retry — no tasks stuck at fetch.');
      setTimeout(() => setDiagToast(''), 4000);
      return;
    }
    if (!window.confirm(
      `Retry auto-fetch for up to ${Math.min(count, 500)} stuck task(s) ` +
      `in this project? Each task will re-scan NAS for its playblast ` +
      `and update its own status chip within 30-60s.`,
    )) return;
    setFetchRetryBusy(true);
    setDiagToast('');
    try {
      const r = await api.post('/reviews/admin/batch-auto-fetch', {
        project_id: filters.project_id,
        limit: 500,
      });
      setDiagToast(`✓ ${r.data?.tldr || 'Auto-fetch retry dispatched'}`);
      setTimeout(() => load(true), 3000);
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') { setFetchRetryBusy(false); return; }
      setDiagToast(`✗ ${fe.message}`);
    } finally {
      setFetchRetryBusy(false);
      setTimeout(() => setDiagToast(''), 8000);
    }
  }, [api, filters.project_id, diag, load]);
  const batchRunSupervisor = useCallback(async (kind = 'both') => {
    if (!filters.project_id) {
      setDiagToast('✗ Pick a project from the filter first.');
      setTimeout(() => setDiagToast(''), 5000);
      return;
    }
    const pendingCount = diag?.funnel?.pending_no_review || 0;
    const failedCount = diag?.funnel?.reviews_failed || 0;
    const total = pendingCount + failedCount;
    if (total === 0) {
      setDiagToast('Nothing pending — every submission already has a supervisor verdict.');
      setTimeout(() => setDiagToast(''), 4000);
      return;
    }
    if (!window.confirm(
      `Dispatch ${kind === 'both' ? 'Layout + Animation' : kind} supervisor for ` +
      `up to ${Math.min(total, 200)} submissions in this project? ` +
      `Results stream into the feed over 1-3 min.`,
    )) return;
    setBatchBusy(true);
    setDiagToast('');
    try {
      const r = await api.post('/reviews/admin/supervisor-batch-run', {
        project_id: filters.project_id,
        supervisor_kind: kind,
        limit: 200,
      });
      setDiagToast(`✓ ${r.data?.tldr || 'Batch dispatch queued'}`);
      setTimeout(() => load(true), 2500);
    } catch (e) {
      const fe = formatApiError(e);
      if (fe.kind === 'auth_expired') { setBatchBusy(false); return; }
      setDiagToast(`✗ ${fe.message}`);
    } finally {
      setBatchBusy(false);
      setTimeout(() => setDiagToast(''), 8000);
    }
  }, [api, filters.project_id, diag, load]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s + re-render the "Xm ago" labels every 30s
  useEffect(() => {
    const i = setInterval(() => { load(true); setNow(Date.now()); }, 30_000);
    return () => clearInterval(i);
  }, [load]);

  const summary = useMemo(() => {
    const approved = items.filter(i => i.decision === 'APPROVED').length;
    const revise = items.filter(i => i.decision === 'MINOR_REVISION' || i.decision === 'MAJOR_REVISION').length;
    const rejected = items.filter(i => i.decision === 'REJECTED').length;
    return { approved, revise, rejected, total: items.length };
  }, [items]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6" data-testid="supervisor-feed-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="flex items-center gap-3 text-2xl font-semibold text-zinc-100">
              <Activity className="w-6 h-6 text-amber-400" />
              Supervisor Feed
            </h1>
            <p className="text-[12px] text-zinc-500 mt-1">
              Every Layout / Animation supervisor verdict — admin + PM real-time pulse. Auto-refresh 30s.
            </p>
          </div>
          <button
            onClick={() => load()}
            disabled={refreshing}
            data-testid="supfeed-refresh-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded border border-zinc-700 hover:bg-zinc-800 text-[12px] text-zinc-300 disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="supfeed-summary">
          <SummaryChip label="Total" value={summary.total} cls="border-zinc-700 text-zinc-300" />
          <SummaryChip label="Approved" value={summary.approved} cls="border-emerald-500/40 text-emerald-300" />
          <SummaryChip label="Revisions" value={summary.revise} cls="border-amber-500/40 text-amber-300" />
          <SummaryChip label="Rejected" value={summary.rejected} cls="border-rose-500/40 text-rose-300" />
        </div>

        {/* ── Pipeline Diagnostics (Feb 10, 2026 founder ask) ─────────
            Shows admins WHY the feed is empty: stuck task types, failed
            reviews, pending submissions. With one-click admin overrides
            to unblock without engineering help. */}
        {diag && (
          <div className="mb-5 bg-zinc-900 border border-zinc-800 rounded" data-testid="supfeed-diagnostics">
            <button
              onClick={() => setDiagOpen(o => !o)}
              data-testid="diag-toggle"
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/50"
            >
              <div className="flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-cyan-400" />
                <span className="text-[13px] font-medium text-zinc-200">Pipeline Diagnostics</span>
                {(() => {
                  const needAttn = (diag.funnel?.pending_no_review || 0)
                    + (diag.funnel?.reviews_failed || 0)
                    + (diag.stuck_at_fetch?.length || 0);
                  return needAttn > 0 ? (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      {needAttn} need attention
                    </span>
                  ) : null;
                })()}
              </div>
              <ChevronRight className={`w-4 h-4 text-zinc-500 transition ${diagOpen ? 'rotate-90' : ''}`} />
            </button>

            {diagOpen && (
              <div className="px-4 pb-4 space-y-4 border-t border-zinc-800/60">
                {/* ── UPSTREAM funnel (Jun 13 2026): shows why the
                    Supervisor Feed is empty — most shots never made it
                    past auto-fetch. Only renders when a project is
                    filtered because the upstream counts are project-
                    scoped (tasks collection is too large to scan
                    globally). */}
                {diag.upstream && Object.keys(diag.upstream).length > 0 && (
                  <div className="pt-3" data-testid="diag-upstream-funnel">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[11px] uppercase tracking-widest text-zinc-400">
                        Upstream — Allotted Tasks → Playblast Fetch
                      </h3>
                      {((diag.upstream.tasks_fetch_failed || 0)
                        + (diag.upstream.tasks_fetch_never || 0)) > 0 && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40">
                          {(diag.upstream.tasks_fetch_failed || 0)
                            + (diag.upstream.tasks_fetch_never || 0)} stuck at fetch
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                      <FunnelStat label="Allotted" value={diag.upstream.allotted_tasks} cls="text-zinc-200" />
                      <FunnelStat label="Active" value={diag.upstream.active_tasks} cls="text-zinc-300" />
                      <FunnelStat label="Fetched ✓" value={diag.upstream.tasks_fetch_success} cls="text-emerald-300" />
                      <FunnelStat label="Fetch failed" value={diag.upstream.tasks_fetch_failed} cls="text-rose-300" />
                      <FunnelStat label="Fetch pending" value={diag.upstream.tasks_fetch_pending} cls="text-amber-300" />
                      <FunnelStat label="Never tried" value={diag.upstream.tasks_fetch_never} cls="text-zinc-300" />
                    </div>
                  </div>
                )}

                {/* Funnel — supervisor-side (downstream of fetch) */}
                <div data-testid="diag-funnel-wrap">
                  <h3 className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">
                    Downstream — Submissions → Supervisor Verdicts
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="diag-funnel">
                    <FunnelStat label="Eligible subs" value={diag.funnel?.eligible_submissions} cls="text-zinc-200" />
                    <FunnelStat label="Reviews started" value={diag.funnel?.reviews_started} cls="text-zinc-300" />
                    <FunnelStat label="Completed" value={diag.funnel?.reviews_completed} cls="text-emerald-300" />
                    <FunnelStat label="Failed" value={diag.funnel?.reviews_failed} cls="text-rose-300" />
                    <FunnelStat label="Pending" value={diag.funnel?.pending_no_review} cls="text-amber-300" />
                    <FunnelStat label="Stuck" value={diag.funnel?.stuck_in_progress} cls="text-orange-300" />
                  </div>
                </div>

                {diagToast && (
                  <div data-testid="diag-toast" className={`text-[12px] px-3 py-2 rounded border ${diagToast.startsWith('✓') ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/10 text-rose-300 border-rose-500/30'}`}>
                    {diagToast}
                  </div>
                )}

                {/* ── Stuck at fetch (Jun 13 2026) — tasks blocked
                    BEFORE they could reach the supervisor. Shows each
                    failed/pending task with its specific fetch error
                    so the producer knows whether it's a NAS file
                    missing, wrong nomenclature, bridge offline, etc.
                    "Retry stuck fetch" button mass-re-fires the same
                    /admin/batch-auto-fetch the inbox header uses. */}
                {filters.project_id && diag.stuck_at_fetch?.length > 0 && (
                  <div data-testid="diag-stuck-fetch-section">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[11px] uppercase tracking-widest text-rose-300">
                        Stuck at Fetch — {diag.stuck_at_fetch.length} task{diag.stuck_at_fetch.length !== 1 ? 's' : ''} blocked before supervisor
                      </h3>
                      <button
                        onClick={() => retryStuckFetch()}
                        disabled={fetchRetryBusy}
                        data-testid="diag-retry-stuck-fetch-btn"
                        className="px-2.5 py-1 text-[11px] rounded border border-rose-500/40 text-rose-200 hover:bg-rose-500/10 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {fetchRetryBusy
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <RefreshCw className="w-3 h-3" />}
                        {fetchRetryBusy ? 'Retrying…' : 'Retry all stuck fetches'}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {diag.stuck_at_fetch.slice(0, 25).map(t => (
                        <StuckFetchRow key={t.task_id} row={t} />
                      ))}
                      {diag.stuck_at_fetch.length > 25 && (
                        <p className="text-[11px] text-zinc-500 italic pl-1">
                          + {diag.stuck_at_fetch.length - 25} more — click &ldquo;Retry all&rdquo; to re-fire every stuck fetch.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Batch dispatch — founder spec Feb 11 2026.
                    Visible only when a project is filtered AND there's
                    something pending/failed to dispatch. */}
                {filters.project_id
                    && ((diag.funnel?.pending_no_review || 0) > 0
                        || (diag.funnel?.reviews_failed || 0) > 0) && (
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded border border-cyan-500/30 bg-cyan-500/5">
                    <span className="text-[11px] text-cyan-300 uppercase tracking-widest">
                      Batch dispatch
                    </span>
                    <span className="text-[11px] text-zinc-400">
                      {(diag.funnel?.pending_no_review || 0)
                        + (diag.funnel?.reviews_failed || 0)} submission(s) need a verdict
                    </span>
                    <div className="flex gap-1.5 ml-auto">
                      <button
                        onClick={() => batchRunSupervisor('layout')}
                        disabled={batchBusy}
                        data-testid="batch-run-layout-btn"
                        className="px-2.5 py-1 text-[11px] rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50"
                      >
                        {batchBusy ? 'Dispatching…' : 'Run Layout · all pending'}
                      </button>
                      <button
                        onClick={() => batchRunSupervisor('animation')}
                        disabled={batchBusy}
                        data-testid="batch-run-animation-btn"
                        className="px-2.5 py-1 text-[11px] rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50"
                      >
                        {batchBusy ? 'Dispatching…' : 'Run Animation · all pending'}
                      </button>
                      <button
                        onClick={() => batchRunSupervisor('both')}
                        disabled={batchBusy}
                        data-testid="batch-run-both-btn"
                        className="px-2.5 py-1 text-[11px] rounded border border-cyan-500/60 bg-cyan-500/10 text-cyan-200 font-semibold hover:bg-cyan-500/20 disabled:opacity-50"
                      >
                        {batchBusy ? 'Dispatching…' : 'Run Layout + Animation'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pending submissions */}
                {diag.pending?.length > 0 && (
                  <div data-testid="diag-pending-section">
                    <h3 className="text-[11px] uppercase tracking-widest text-amber-300 mb-2">
                      Pending — {diag.pending.length} submission{diag.pending.length !== 1 ? 's' : ''} without a supervisor verdict
                    </h3>
                    <div className="space-y-2">
                      {diag.pending.map(p => (
                        <PendingRow
                          key={p.submission_id}
                          row={p}
                          busy={busyIds[p.submission_id] || busyIds[p.task_id]}
                          onRerun={() => rerunSupervisor(p.submission_id)}
                          onForce={(tt) => p.task_id && forceTaskType(p.task_id, tt)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed reviews */}
                {diag.failed?.length > 0 && (
                  <div data-testid="diag-failed-section">
                    <h3 className="text-[11px] uppercase tracking-widest text-rose-300 mb-2">
                      Failed — {diag.failed.length} supervisor run{diag.failed.length !== 1 ? 's' : ''} errored out
                    </h3>
                    <div className="space-y-2">
                      {diag.failed.map(f => (
                        <FailedRow
                          key={f.submission_id}
                          row={f}
                          busy={busyIds[f.submission_id]}
                          onRerun={() => rerunSupervisor(f.submission_id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {(!diag.pending?.length && !diag.failed?.length && !diag.stuck?.length && !diag.stuck_at_fetch?.length) && (
                  <div className="text-[12px] text-emerald-300 flex items-center gap-2 py-2" data-testid="diag-all-clear">
                    <CheckCircle2 className="w-4 h-4" />
                    Pipeline is healthy — every active task has been fetched and every eligible submission has a supervisor verdict.
                  </div>
                )}
                {(!diag.pending?.length && !diag.failed?.length && !diag.stuck?.length) && diag.stuck_at_fetch?.length > 0 && (
                  <div className="text-[12px] text-amber-300 flex items-center gap-2 py-2" data-testid="diag-fetch-stuck-warning">
                    <AlertTriangle className="w-4 h-4" />
                    Supervisor side is healthy, but {diag.stuck_at_fetch.length} task{diag.stuck_at_fetch.length !== 1 ? 's are' : ' is'} stuck at the fetch step (see above).
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center flex-wrap gap-2 mb-4 bg-zinc-900 border border-zinc-800 rounded p-3">
          <Filter className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Filters</span>

          <select
            value={filters.project_id}
            onChange={e => setFilters({ ...filters, project_id: e.target.value })}
            data-testid="supfeed-filter-project"
            className="bg-zinc-950 border border-zinc-700 text-zinc-200 text-[12px] rounded px-2 py-1"
          >
            <option value="">All projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={filters.supervisor_kind}
            onChange={e => setFilters({ ...filters, supervisor_kind: e.target.value })}
            data-testid="supfeed-filter-kind"
            className="bg-zinc-950 border border-zinc-700 text-zinc-200 text-[12px] rounded px-2 py-1"
          >
            <option value="">Layout + Animation</option>
            <option value="layout">Layout only</option>
            <option value="animation">Animation only</option>
          </select>

          <select
            value={filters.decision}
            onChange={e => setFilters({ ...filters, decision: e.target.value })}
            data-testid="supfeed-filter-decision"
            className="bg-zinc-950 border border-zinc-700 text-zinc-200 text-[12px] rounded px-2 py-1"
          >
            <option value="">All decisions</option>
            <option value="APPROVED">Approved</option>
            <option value="MINOR_REVISION">Minor revision</option>
            <option value="MAJOR_REVISION">Major revision</option>
            <option value="REJECTED">Rejected</option>
          </select>

          {(filters.project_id || filters.supervisor_kind || filters.decision) && (
            <button
              onClick={() => setFilters({ project_id: '', supervisor_kind: '', decision: '' })}
              data-testid="supfeed-filter-clear"
              className="text-[11px] text-amber-400 hover:text-amber-300 underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="text-center text-zinc-500 py-10" data-testid="supfeed-loading">Loading verdicts…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded" data-testid="supfeed-empty">
            <Activity className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-[13px]">No supervisor verdicts yet for this filter.</p>
            <p className="text-zinc-600 text-[11px] mt-1">As soon as an artist submits a shot for REVIEW, the supervisor's decision lands here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="supfeed-list">
            {items.map(row => (
              <FeedRow key={row.review_id} row={row} now={now} onOpen={() => nav(`/reviews?submission_id=${row.submission_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SummaryChip = ({ label, value, cls }) => (
  <div className={`flex items-center justify-between px-3 py-2 rounded border bg-zinc-900 ${cls}`}>
    <span className="text-[10px] uppercase tracking-widest opacity-70">{label}</span>
    <span className="text-xl font-mono">{value}</span>
  </div>
);

const FunnelStat = ({ label, value, cls }) => (
  <div className="flex flex-col gap-0.5 bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
    <span className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</span>
    <span className={`text-base font-mono ${cls}`}>{value ?? 0}</span>
  </div>
);

function PendingRow({ row, busy, onRerun, onForce }) {
  const inferred = row.inferred_task_type;
  return (
    <div
      data-testid={`diag-pending-row-${row.submission_id}`}
      className="flex flex-col gap-2 bg-zinc-950 border border-amber-500/20 rounded p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-zinc-200 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-amber-200">{row.shot_code || '—'}</span>
          <span className="text-zinc-600">·</span>
          <span>{row.artist_name || 'Unknown artist'}</span>
          <span className="text-zinc-700 text-[10px]">R{row.round || 1}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-[10px] uppercase font-mono text-zinc-500">
            task_type: <span className="text-rose-300">{row.task_type || '—'}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {inferred && (
            <button
              onClick={() => onForce(inferred)}
              disabled={!!busy}
              data-testid={`diag-force-${row.submission_id}`}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 disabled:opacity-50"
              title={`Set task_type to '${inferred}' so the supervisor can dispatch`}
            >
              {busy === 'force' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
              Force → {inferred.toUpperCase()}
            </button>
          )}
          <button
            onClick={onRerun}
            disabled={!!busy}
            data-testid={`diag-rerun-${row.submission_id}`}
            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50"
          >
            {busy === 'rerun' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Re-run
          </button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-400 leading-snug">{row.reason}</p>
    </div>
  );
}

function StuckFetchRow({ row }) {
  // Map auto_fetch_error to a 1-line plain-English hint so producers
  // don't need to parse engineering-speak from the chip.
  const err = (row.auto_fetch_error || '').toLowerCase();
  let category = 'Unknown';
  let categoryCls = 'text-zinc-400';
  if (!row.auto_fetch_error && row.auto_fetch_status === 'never') {
    category = 'Never tried';
    categoryCls = 'text-zinc-400';
  } else if (err.includes('nomenclature')) {
    category = 'Filename violates studio convention';
    categoryCls = 'text-orange-300';
  } else if (err.includes('no playblast') || err.includes('no_matching_file')) {
    category = 'Playblast missing on NAS';
    categoryCls = 'text-rose-300';
  } else if (err.includes('folder_not_found') || err.includes('folder')) {
    category = 'NAS folder not found';
    categoryCls = 'text-rose-300';
  } else if (err.includes('bridge') || err.includes('offline')) {
    category = 'Bridge / NAS unreachable';
    categoryCls = 'text-rose-300';
  } else if (err.includes('storage') || err.includes('upload')) {
    category = 'Cloud upload failed';
    categoryCls = 'text-rose-300';
  } else if (err.includes('too large') || err.includes('file_too_large')) {
    category = 'File too large (>100MB)';
    categoryCls = 'text-amber-300';
  } else if (err.includes('no_assignee')) {
    category = 'Task has no assignee';
    categoryCls = 'text-amber-300';
  } else if (err.includes('unsupported_task_type') || err.includes('task_type')) {
    category = 'Task type not auto-fetch eligible';
    categoryCls = 'text-amber-300';
  } else if (err) {
    category = 'Fetch failure';
    categoryCls = 'text-rose-300';
  }

  return (
    <div
      data-testid={`diag-stuck-fetch-row-${row.task_id}`}
      className="flex flex-col gap-1.5 bg-zinc-950 border border-rose-500/15 rounded p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-zinc-200 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-rose-200">{row.shot_code || row.title || '—'}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-[10px] uppercase font-mono text-zinc-500">{row.task_type || '—'}</span>
          {row.assignee_name && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="text-zinc-400">{row.assignee_name}</span>
            </>
          )}
          <span className="text-zinc-700">·</span>
          <span className={`text-[10px] uppercase tracking-widest ${categoryCls}`}>
            {category}
          </span>
        </div>
      </div>
      {row.auto_fetch_error && (
        <p className="text-[11px] text-zinc-400 font-mono leading-snug whitespace-pre-wrap line-clamp-2">
          {row.auto_fetch_error}
        </p>
      )}
    </div>
  );
}

function FailedRow({ row, busy, onRerun }) {
  return (
    <div
      data-testid={`diag-failed-row-${row.submission_id}`}
      className="flex flex-col gap-2 bg-zinc-950 border border-rose-500/20 rounded p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-zinc-200 flex items-center gap-2 flex-wrap">
          <XCircle className="w-3.5 h-3.5 text-rose-400" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{row.supervisor_kind}</span>
          <span className="font-mono text-zinc-500 text-[11px]">{(row.submission_id || '').slice(0, 8)}</span>
        </div>
        <button
          onClick={onRerun}
          disabled={!!busy}
          data-testid={`diag-rerun-${row.submission_id}`}
          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50"
        >
          {busy === 'rerun' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
          Re-run
        </button>
      </div>
      {row.error && (
        <p className="text-[11px] text-rose-300/80 font-mono leading-snug whitespace-pre-wrap">{row.error}</p>
      )}
    </div>
  );
}

function FeedRow({ row, now: _now, onOpen }) {
  const dec = DECISION_BADGES[row.decision] || DECISION_BADGES.MINOR_REVISION;
  const Icon = dec.icon;
  return (
    <div
      data-testid={`supfeed-row-${row.review_id}`}
      className="bg-zinc-900 border border-zinc-800 rounded p-4 hover:border-zinc-700 transition"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">{row.project_code || row.project_name || '—'}</span>
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-200 font-medium" data-testid={`supfeed-artist-${row.review_id}`}>{row.artist_name}</span>
            <span className="text-zinc-600 text-[12px]">submitted</span>
            <span className="font-mono text-amber-200 text-[13px]" data-testid={`supfeed-shot-${row.review_id}`}>{row.shot_code}</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1">{row.supervisor_kind}</span>
            {row.had_layout_reference === true && row.supervisor_kind === 'animation' && (
              <span title="Animation supervisor compared against the approved Layout MP4 in addition to the CBA" className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 uppercase">
                Layout-ref
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded border text-[11px] font-mono ${dec.cls}`}>
              <Icon className="w-3 h-3" />
              {dec.label}
            </span>
            <span className="text-[12px] font-mono text-zinc-300" data-testid={`supfeed-score-${row.review_id}`}>
              {row.overall_score}<span className="text-zinc-600">/{row.max_score}</span>
            </span>
            {row.pin_count > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                <MapPin className="w-3 h-3" />
                {row.pin_count} note{row.pin_count !== 1 ? 's' : ''}
              </span>
            )}
            <span className="flex items-center gap-1 text-[11px] text-zinc-500 ml-auto">
              <Clock className="w-3 h-3" />
              {timeAgo(row.completed_at)}
            </span>
          </div>

          {row.top_pins?.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid={`supfeed-pins-${row.review_id}`}>
              {row.top_pins.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-zinc-300">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${SEV_DOT[p.severity] || 'bg-zinc-500'}`} />
                  <span className="line-clamp-2 flex-1">{p.text}</span>
                  {Number.isFinite(p.timecode_sec) && (
                    <span className="font-mono text-[10px] text-zinc-500 flex-shrink-0">
                      {`${Math.floor(p.timecode_sec)}s`}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={onOpen}
          data-testid={`supfeed-open-${row.review_id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[12px] font-medium"
        >
          <Film className="w-3.5 h-3.5" />
          Open in Approvals
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
